/* Minimal header/drawer behavior.
 * Key goal: avoid cloning/replacing header HTML at runtime (no innerHTML copies of nav),
 * because that can cause menu "flashes" and unexpected swaps on live pages.
 */

if (typeof MainHeader !== 'function') {
  class MainHeader extends HTMLElement {
    constructor() {
      super();
      this._mounted = false;
      this._onResize = null;
      this._onEsc = null;
      this._onOverlayClick = null;
      this._rtl = document.documentElement.getAttribute('dir') === 'rtl';
    }

    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;
      this.mount();
    }

    disconnectedCallback() {
      this.unmount();
    }

    mount() {
      this._setupSidebarHandles();
      this._setupInertElems();
      this._setupOverlayClose();
      this._setupDrawerHeightVar();
      this._setupSubmenuAlignment();
      this._setupTouchMenuHelpers();
      this._setupKeyboardMenuHelpers();
      this._setupPredictiveSearch();
    }

    unmount() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onEsc) document.removeEventListener('keydown', this._onEsc);
      if (this._onOverlayClick && document.querySelector('.site-overlay')) {
        document.querySelector('.site-overlay').removeEventListener('click', this._onOverlayClick);
      }
    }

    _setupSidebarHandles() {
      // Bind only once per element.
      this.querySelectorAll('[data-js-sidebar-handle]').forEach((elm) => {
        if (elm.dataset.boundSidebarHandle === 'true') return;
        elm.dataset.boundSidebarHandle = 'true';

        const controls = elm.getAttribute('aria-controls');
        if (!controls) return;
        const drawer = document.getElementById(controls);
        if (!drawer) return;

        const open = (e) => {
          e?.preventDefault?.();
          elm.setAttribute('aria-expanded', 'true');
          drawer.show?.();
        };

        elm.addEventListener('click', open);
        elm.addEventListener('keyup', (e) => {
          if (e.keyCode === window.KEYCODES?.RETURN) {
            window.lastFocusedElm = elm;
            open(e);
            drawer.querySelector('[data-js-close]')?.focus?.();
          }
        });
      });
    }

    _setupInertElems() {
      // Mark main sections inertable for drawers.
      document.querySelectorAll('#main > div').forEach((elm) => {
        if (!elm.classList.contains('inert-inside')) {
          elm.setAttribute('data-js-inert', '');
        }
      });
      window.inertElems = document.querySelectorAll('[data-js-inert]');
    }

    _setupOverlayClose() {
      const overlay = document.querySelector('.site-overlay');
      this._onOverlayClick = () => {
        document.querySelector('sidebar-drawer.sidebar--opened')?.hide?.();
      };
      overlay?.addEventListener('click', this._onOverlayClick);

      this._onEsc = (e) => {
        if (e.keyCode === window.KEYCODES?.ESC) {
          const opened = document.querySelector('sidebar-drawer.sidebar--opened');
          if (opened) {
            opened.hide?.();
            window.lastFocusedElm?.focus?.();
            window.lastFocusedElm = null;
          }
        }
      };
      document.addEventListener('keydown', this._onEsc);
    }

    _setupDrawerHeightVar() {
      const rootHeight = document.getElementById('root-height');
      if (!rootHeight) return;
      const update = () => {
        rootHeight.innerHTML = `:root {\n  --window-height: ${window.innerHeight}px;\n}`;
      };
      this._onResize = debounce(update, 200);
      window.addEventListener('resize', this._onResize);
      update();
    }

    _setupSubmenuAlignment() {
      // Scope to this header so we don't attach handlers to other navs.
      this.querySelectorAll('.site-nav.style--classic .has-submenu').forEach((li) => {
        li.addEventListener('mouseover', () => {
          const normalMenu = li.querySelector('.normal-menu');
          if (!normalMenu) return;
          const rect = li.getBoundingClientRect();
          normalMenu.style.left = `${this._rtl ? rect.right : rect.left}px`;
        });
      });
    }

    _setupTouchMenuHelpers() {
      const closeTouchSubmenus = (focusedElm, focusedParent = null) => {
        this.querySelectorAll('.style--classic li.focus').forEach((elm) => {
          if (!(elm === focusedElm || elm === focusedParent)) {
            elm.classList.remove('focus');
          }
        });
      };

      let babyMenuTouch = false;
      this.querySelectorAll('.style--classic .has-babymenu').forEach((elm) => {
        elm.addEventListener(
          'touchstart',
          () => {
            elm.firstElementChild.style.pointerEvents = 'none';
            elm.classList.toggle('focus');
            babyMenuTouch = true;
            closeTouchSubmenus(elm, elm.closest('.has-submenu'));
          },
          { passive: true }
        );
      });

      this.querySelectorAll('.style--classic .has-submenu').forEach((elm) => {
        if (elm.classList.contains('mega-link')) return;
        elm.addEventListener(
          'touchstart',
          () => {
            elm.firstElementChild.style.pointerEvents = 'none';
            if (!babyMenuTouch) {
              elm.classList.toggle('focus');
              closeTouchSubmenus(elm);
            }
            babyMenuTouch = false;
          },
          { passive: true }
        );
      });
    }

    _setupKeyboardMenuHelpers() {
      this.querySelectorAll('.site-nav.style--classic .has-submenu > a').forEach((childEl) => {
        const li = childEl.parentNode;
        li.addEventListener('keydown', (e) => {
          if (e.keyCode !== window.KEYCODES?.RETURN) return;
          if (!e.target.classList.contains('no-focus-link')) e.preventDefault();

          li.classList.toggle('focus');
          e.target.setAttribute('aria-expanded', li.classList.contains('focus') ? 'true' : 'false');
        });

        li.querySelector('.submenu-holder > li:last-child a, .submenu-masonry > li:last-child a')?.addEventListener('focusout', (e) => {
          if (!li.classList.contains('focus')) return;
          li.classList.remove('focus');
          childEl.setAttribute('aria-expanded', 'false');
        });
      });

      this.querySelectorAll('.site-nav.style--classic .has-babymenu:not(.mega-link) > a').forEach((childEl) => {
        const li = childEl.parentNode;
        li.addEventListener('keydown', (e) => {
          if (e.keyCode !== window.KEYCODES?.RETURN) return;
          if (!e.target.classList.contains('no-focus-link')) e.preventDefault();

          li.classList.toggle('focus');
          e.target.setAttribute('aria-expanded', li.classList.contains('focus') ? 'true' : 'false');
        });

        li.querySelector('.babymenu li:last-child a')?.addEventListener('focusout', () => {
          li.classList.remove('focus');
          childEl.setAttribute('aria-expanded', 'false');
        });
      });
    }

    _setupPredictiveSearch() {
      const featuresEl = document.getElementById('shopify-features');
      if (!featuresEl) return;
      let features = null;
      try {
        features = JSON.parse(featuresEl.text);
      } catch {
        return;
      }
      if (!features?.predictiveSearch) {
        this.querySelector('search-form [data-js-search-input] + button')?.classList.add('button--invisibile-trigger');
        return;
      }

      this.querySelectorAll('search-form [data-js-search-input]').forEach((elm) => {
        elm.addEventListener('focus', () => {
          const overlayId = elm.dataset.jsFocusOverlay;
          if (overlayId) document.getElementById(overlayId)?.classList.add('active');
          if (!document.body.classList.contains('predictive-script-loaded')) {
            document.body.classList.add('predictive-script-loaded');
            const predictiveSearchJS = document.createElement('script');
            predictiveSearchJS.src = KROWN.settings.predictive_search_script;
            document.head.appendChild(predictiveSearchJS);
          }
        });

        elm.addEventListener('keydown', (e) => {
          if (e.keyCode === window.KEYCODES?.TAB) {
            const overlayId = elm.dataset.jsFocusOverlay;
            if (overlayId) document.getElementById(overlayId)?.classList.remove('active');
          }
        });
      });

      window.addEventListener('load', () => {
        this.querySelectorAll('.search-results-overlay').forEach((elm) => {
          elm.style = '';
        });
      });
    }
  }

  if (typeof customElements.get('main-header') === 'undefined') {
    customElements.define('main-header', MainHeader);
  }
}

if (typeof SidebarDrawer !== 'function') {
  class SidebarDrawer extends HTMLElement {
    constructor() {
      super();
      this.querySelector('[data-js-close]')?.addEventListener('click', () => this.hide());
    }

    show() {
      this.opened = true;
      this.removeAttribute('aria-hidden');
      document.body.classList.add('sidebar-opened');
      if (this.classList.contains('sidebar--right')) document.body.classList.add('sidebar-opened--right');
      if (this.classList.contains('sidebar--left')) document.body.classList.add('sidebar-opened--left');

      this.style.display = 'grid';
      setTimeout(() => {
        this.classList.add('sidebar--opened');
        window.inertElems?.forEach?.((elm) => elm.setAttribute('inert', ''));
      }, 15);

      if (this.id === 'site-cart-sidebar') {
        document.querySelector('#cart-recommendations css-slider')?.resetSlider?.();
      }
    }

    hide() {
      this.opened = false;
      this.classList.remove('sidebar--opened');

      document.body.classList.remove('sidebar-opened', 'sidebar-opened--left', 'sidebar-opened--right');
      window.inertElems?.forEach?.((elm) => elm.removeAttribute('inert'));

      document.querySelector(`[aria-controls="${this.id}"]`)?.setAttribute('aria-expanded', 'false');
      setTimeout(() => {
        this.style.display = 'none';
      }, 501);
    }
  }

  if (typeof customElements.get('sidebar-drawer') === 'undefined') {
    customElements.define('sidebar-drawer', SidebarDrawer);
  }
}

if (typeof MobileNavigation !== 'function') {
  class MobileNavigation extends HTMLElement {
    constructor() {
      super();

      this._openedFirstSubmenu = false;
      this._openedSecondSubmenu = false;

      this.querySelectorAll('.has-submenu > a').forEach((elm) => {
        elm.addEventListener('click', (e) => {
          e.preventDefault();
          if (!this._openedFirstSubmenu) {
            this._openedFirstSubmenu = true;
            this.classList.add('opened-first-submenu');
            this.closest('sidebar-drawer')?.scrollTo?.({ top: 0 });
          }
          e.target.closest('li')?.classList.add('opened');
          this._resizeContainer();
        });
      });

      this.querySelectorAll('.has-babymenu > a').forEach((elm) => {
        elm.addEventListener('click', (e) => {
          e.preventDefault();
          if (!this._openedSecondSubmenu) {
            this._openedSecondSubmenu = true;
            this.classList.add('opened-second-submenu');
            this.closest('sidebar-drawer')?.scrollTo?.({ top: 0 });
          }
          e.target.closest('li')?.classList.add('opened');
          this._resizeContainer();
        });
      });

      this.querySelectorAll('.submenu-back a').forEach((elm) => {
        elm.addEventListener('click', (e) => {
          if (this._openedSecondSubmenu) {
            this._openedSecondSubmenu = false;
            this.classList.remove('opened-second-submenu');
            this._resizeContainer();
          } else if (this._openedFirstSubmenu) {
            this._openedFirstSubmenu = false;
            this.classList.remove('opened-first-submenu');
            this._resizeContainer(true);
          }
          this.closest('sidebar-drawer')?.scrollTo?.({ top: 0 });
          setTimeout(() => {
            e.target.closest('li.opened')?.classList.remove('opened');
          }, 301);
          e.preventDefault();
        });
      });

      if (this.dataset.showHeaderActions === 'true') {
        const src = document.querySelector('[data-js-header-actions]');
        if (src) {
          const mobileNavActions = document.createElement('div');
          mobileNavActions.classList = 'header-actions flex-buttons';
          mobileNavActions.innerHTML = src.innerHTML;
          this.querySelector('nav')?.prepend(mobileNavActions);

          mobileNavActions.querySelectorAll('[id]').forEach((elm) => {
            elm.id = `${elm.id}-mobile`;
          });
          mobileNavActions.querySelectorAll('[data-modal]').forEach((elm) => {
            elm.addEventListener('keydown', (e) => {
              if (e.keyCode === window.KEYCODES?.RETURN) window.lastFocusedElm = elm;
            });
            elm.addEventListener('click', (e) => {
              e.preventDefault();
              document.querySelector('.sidebar--opened')?.hide?.();
              document.getElementById(elm.getAttribute('aria-controls'))?.show?.();
            });
          });
        }
      }
    }

    _resizeContainer(main = false) {
      if (main) {
        this.style.height = 'auto';
        return;
      }
      if (this._openedSecondSubmenu) {
        this.style.height = `${this.querySelector('.has-babymenu.opened .babymenu')?.scrollHeight || 0}px`;
      } else if (this._openedFirstSubmenu) {
        this.style.height = `${this.querySelector('.has-submenu.opened .submenu')?.scrollHeight || 0}px`;
      }
    }
  }

  if (typeof customElements.get('mobile-navigation') === 'undefined') {
    customElements.define('mobile-navigation', MobileNavigation);
  }
}

if (typeof ScrollableNavigation !== 'function') {
  class ScrollableNavigation extends HTMLElement {
    constructor() {
      super();
      this.linkList = this.querySelector('.link-list');
      this.header = this.parentNode;
      window.addEventListener(
        'resize',
        debounce(() => {
          this.checkNav();
        }, 200)
      );
      this.checkNav();

      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      this.parentNode.querySelector('.scrollable-navigation-button--left')?.addEventListener('click', () => {
        this.scroll({ top: 0, left: this.scrollLeft - (rtl ? -100 : 100), behavior: 'smooth' });
      });
      this.parentNode.querySelector('.scrollable-navigation-button--right')?.addEventListener('click', () => {
        this.scroll({ top: 0, left: this.scrollLeft + (rtl ? -100 : 100), behavior: 'smooth' });
      });
    }

    checkNav() {
      if (!this.linkList) return;
      if (this.linkList.scrollWidth > this.offsetWidth) this.header.classList.add('scrolling-navigation-enabled');
      else this.header.classList.remove('scrolling-navigation-enabled');
    }
  }

  if (typeof customElements.get('scrollable-navigation') === 'undefined') {
    customElements.define('scrollable-navigation', ScrollableNavigation);
  }
}

